import { ReactNode, useContext, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { fetchRest } from '@/common/api/fetcher'
import { GetTagSnippetResponse } from '@/common/api/params/ugc'
import { ugcDisplayRulesPath } from '@/common/app/path'
import { copyTextToClipboard } from '@/common/util/clipboard'

import Code from '@/components/view/atoms/code/Code'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

/**
 * どのページに・どのタグを貼ると・何が出るか（2026-09-29 追加）。
 * 表示内容は backend/routers/ugc_display.py の get_ugc_embed と w2ugc.js の挙動に合わせてある
 */
const TAG_GUIDE: { page: string; tag: string; shows: string }[] = [
  {
    page: '全ページの <head>',
    tag: '基本タグ',
    shows: '何も表示しません（下のタグを動かすための読み込みと、効果測定の計測）'
  },
  {
    page: '商品詳細ページ',
    tag: '商品ページ用タグ',
    shows:
      '写真投稿とレビューを混ぜて写真中心に表示。その商品の投稿が先頭、足りない分はサイト全体から'
  },
  {
    page: '商品詳細ページ',
    tag: 'レビュー表示タグ',
    shows: 'その商品のレビューだけ（★の平均・絞り込み・もっと見る付き）'
  },
  {
    page: 'トップページ・特集ページなど',
    tag: '枠ごとの設置タグ（商品以外のページ用）',
    shows:
      'サイト全体の投稿を、枠で決めた並び順（おすすめ順＝AIスコアの高い順）で。「人気のUGC」を出したいときはこちら'
  },
  {
    page: '商品詳細ページ（複数の枠を置きたい）',
    tag: '枠ごとの設置タグ（商品ページ用）',
    shows: '枠で決めた内容（レビューのみ／写真投稿のみ／混在）・レイアウト・件数で'
  }
]

const CONTENT_TYPE_LABELS: Record<string, string> = {
  review: 'レビューのみ',
  ugc: 'レビュー以外',
  mixed: '混在'
}

interface TagSectionProps {
  title: ReactNode
  desc: ReactNode
  code: string
  copy(code: string): void
  children?: ReactNode
}

/** タイトル・説明・コードを1枠にまとめる（埋め込みコード一覧と同じ体裁） */
function TagSection({ title, desc, code, copy, children }: TagSectionProps) {
  return (
    <Area>
      <Heading subText={desc}>
        <ItemAlign gapX={4} align='center'>
          <Icon type={IconType.CODE_SQUARE} color={TextColor.SECONDARY} size={20} />
          {title}
        </ItemAlign>
      </Heading>
      <div className='border-secondary border-t px-6 pb-6'>
        <Code onClick={() => copy(code)}>{code}</Code>
        {children}
      </div>
    </Area>
  )
}

export default function UGCTagEmbed() {
  const { siteId } = useParams()
  const { addMessage } = useContext(ToastMessageContext)
  const [snippet, setSnippet] = useState<GetTagSnippetResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    fetchRest<GetTagSnippetResponse>(`/api/sites/${siteId}/ugc/tag-snippet`)
      .then(setSnippet)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId])

  const copy = (code: string) => {
    copyTextToClipboard(code)
    addMessage({ text: 'コードをコピーしました', type: MessageType.SUCCESS })
  }

  return (
    <Main type={MainLayoutType.SMALL}>
      <PageTitle
        title='配信タグ設定'
        description='ECサイトに貼るタグを発行します。表示する内容・並び順・件数を枠ごとに決めたい場合は、先に「サイト表示枠設定」で枠を作ってください'
      />

      {loading ? (
        <PageListLoading small />
      ) : (
        <Content>
          <Area>
            <Heading subText='設置タグはこのサイト専用です。他サイトのコードを貼っても表示されません'>
              このサイトの配信コード
            </Heading>
            <div className='border-secondary flex flex-wrap items-center gap-x-6 gap-y-2 border-t px-6 py-4'>
              <div className='flex items-center gap-2'>
                <Text size={12} color={TextColor.SECONDARY}>
                  配信コード
                </Text>
                <code className='bg-surface-emphasis text-emphasis rounded-md px-3 py-1 text-sm font-semibold'>
                  {snippet?.distribution_code}
                </code>
              </div>
              <div className='flex items-center gap-2'>
                <Text size={12} color={TextColor.SECONDARY}>
                  ベースURL
                </Text>
                <code className='bg-surface-secondary text-secondary rounded-md px-3 py-1 text-xs'>
                  {snippet?.base_url}
                </code>
              </div>
            </div>
          </Area>

          {/* 2026-09-29 追加：タグの違い・使い分けが分からないという指摘への対応 */}
          <Area>
            <Heading subText='まず基本タグを全ページに貼り、表示したい場所に下のいずれかのタグを貼ります'>
              どのページに、どのタグを貼るか
            </Heading>
            <div className='border-secondary overflow-x-auto border-t px-6 py-4'>
              <table className='text-size-12 w-full border-collapse'>
                <thead>
                  <tr className='text-secondary text-left'>
                    <th className='border-secondary border-b py-1.5 pr-3 font-semibold'>
                      貼るページ
                    </th>
                    <th className='border-secondary border-b py-1.5 pr-3 font-semibold'>
                      貼るタグ
                    </th>
                    <th className='border-secondary border-b py-1.5 font-semibold'>
                      表示されるもの
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {TAG_GUIDE.map((g, i) => (
                    <tr key={i} className='text-primary align-top'>
                      <td className='border-secondary border-b py-1.5 pr-3 whitespace-nowrap'>
                        {g.page}
                      </td>
                      <td className='border-secondary border-b py-1.5 pr-3 whitespace-nowrap'>
                        {g.tag}
                      </td>
                      <td className='border-secondary border-b py-1.5'>{g.shows}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className='text-secondary mt-3 mb-0 text-xs'>
                「商品ページ用タグ」と「レビュー表示タグ」は同じ商品ページに両方貼れます（上に写真、下にレビュー、など）。
                枠ごとの設置タグは{' '}
                <Link to={ugcDisplayRulesPath(Number(siteId))} className='text-link'>
                  サイト表示枠設定
                </Link>{' '}
                で作った枠の数だけ下に表示されます。
              </p>
            </div>
          </Area>

          {snippet?.tags &&
            Object.entries(snippet.tags).map(([key, tag]) => (
              <TagSection
                key={key}
                title={tag.label}
                desc={tag.description}
                code={tag.code}
                copy={copy}
              />
            ))}

          {(snippet?.placements?.length ?? 0) > 0 && (
            <>
              <Area>
                <Heading
                  subText={
                    <>
                      サイト表示枠設定で作った枠です。表示する内容・レイアウト・並び順・件数は枠の設定で決まり、同じページに何個でも置けます。
                      枠ごとに2種類のタグがあります。<b>商品ページ用</b>は <code>{'{商品ID}'}</code>{' '}
                      を商品IDに置き換えて貼り、その商品の投稿を出します。
                      <b>商品以外のページ用</b>
                      はそのまま貼り、サイト全体の投稿を出します（トップページの「人気のUGC」など）。
                      <code>data-placement</code>{' '}
                      の値が枠の識別子と一字でも違うと、何も表示されません。
                    </>
                  }
                >
                  枠ごとの設置タグ
                </Heading>
              </Area>

              {(snippet?.placements ?? []).map((p) => (
                <TagSection
                  key={p.placement_key}
                  title={
                    <ItemAlign gapX={4} align='center'>
                      <code className='bg-surface-emphasis text-emphasis rounded px-1.5 py-0.5 text-sm font-semibold'>
                        {p.placement_key}
                      </code>
                    </ItemAlign>
                  }
                  desc={
                    <>
                      {p.rule_name}／{CONTENT_TYPE_LABELS[p.content_type] || p.content_type}
                      {!p.is_active && (
                        <span className='text-critical'>／停止中（貼っても表示されません）</span>
                      )}
                      <br />
                      商品ページ用（<code>{'{商品ID}'}</code> を置き換えて貼る）
                    </>
                  }
                  code={p.code}
                  copy={copy}
                >
                  {p.code_site_wide && (
                    <div className='mt-3'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        商品以外のページ用（トップページ・特集ページなど。サイト全体の投稿を表示）
                      </Text>
                      <Code onClick={() => copy(p.code_site_wide as string)}>
                        {p.code_site_wide}
                      </Code>
                    </div>
                  )}
                </TagSection>
              ))}
            </>
          )}

          <Area>
            <Heading>設置ガイド</Heading>
            <div className='border-secondary text-secondary border-t px-6 py-4 text-[13px] leading-loose'>
              <p className='m-0'>
                <strong>Step 1:</strong> 基本タグをECサイトの全ページの <code>&lt;head&gt;</code>{' '}
                に設置
              </p>
              <p className='m-0'>
                <strong>Step 2:</strong>{' '}
                商品詳細ページに「商品ページ用タグ」または「レビュー表示タグ」を設置（
                <code>{'{商品ID}'}</code> を商品IDに置き換える）
              </p>
              <p className='m-0'>
                <strong>Step 3（任意）:</strong>{' '}
                トップページなどに表示したい場合や、見た目・件数・並び順を決めたい場合は、サイト表示枠設定で枠を作り、枠ごとの設置タグを貼る
              </p>
              <p className='bg-surface-emphasis text-emphasis mt-3 mb-0 rounded-lg p-2.5 text-xs'>
                💡 どのタグにも追加できる属性：<code>data-template</code>（grid＝格子 /
                carousel＝横スクロール / feed＝縦並び）、
                <code>data-limit</code>（表示件数）、<code>data-title</code>（見出し）
              </p>
            </div>
          </Area>
        </Content>
      )}
    </Main>
  )
}
